import { DateTime } from 'luxon'
import { downloadFile, isSelectingText, paginate, toInitials } from '@utils'
import { logout } from '@utils/sessions'
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
} from '@mui/material'
import { useDebouncedCallback } from 'use-debounce'
import { useEffect, useState } from 'react'
import DashboardErrorView from '@pages/Dashboard/components/DashboardErrorView'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import DashboardLoadingDataView from '@pages/Dashboard/components/DashboardLoadingDataView'
import DownloadButton from '@components/DownloadButton'
import SelectedRowDetailsDialog from '@components/SelectedRowDetailsDialog'
import TableCellSubText from '@components/TableCellSubText'
import TableHeadCell from '@components/TableHeadCell'
import API from '@enums/api'
import { STAFF_REPORTS_URL } from '@enums/api'

export type OverdueOrdersRow = {
    task_id: string
    date_submitted: string
    first_name: string
    last_name: string
    email: string
    media: string
    organization: string | null
    status: string
    origin: string
    deadline: string
    lateness: string
}

const REPORT_API_ENDPOINT = `${STAFF_REPORTS_URL}/overdue-orders`

function prettyDate(isoDate: string) {
    return DateTime.fromISO(isoDate).toFormat('yyyy-LL-dd HH:mm')
}

const OverdueOrdersReportPage = () => {
    const [loading, setLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [rows, setRows] = useState<OverdueOrdersRow[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [selectedRow, setSelectedRow] = useState<OverdueOrdersRow | null>(
        null
    )

    // search state
    const [search, setSearch] = useState('')
    const handleSearchChange = useDebouncedCallback((event) => {
        setSearch(event.target.value.trim())
        setPage(0)
    }, 250)

    // pagination state
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [page, setPage] = useState(0)
    const handlePageChange = (_: unknown, newPage: number) => setPage(newPage)
    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(() => {
        async function load() {
            setLoading(true)
            const res = await API.reports.overdueOrders()
            setLoading(false)
            if (!res.ok) {
                if (res.status === 401) {
                    logout()
                    return
                }

                console.error(res)
                setError('Failed to generate report')
            } else {
                setRows(res.value)
            }
        }

        load()
    }, [])

    // download report as csv
    async function handleDownload() {
        setDownloading(true)
        const date = new Date().toISOString().split('T')[0]
        await downloadFile({
            acceptType: 'text/csv',
            fileURL: REPORT_API_ENDPOINT,
            saveAsFileName: `overdue-orders-report_${date}.csv`,
        })
        setDownloading(false)
    }

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    if (rows === null) {
        return <DashboardLoadingDataView loading={loading} />
    }

    let filteredRows = rows

    if (search) {
        const lSearch = search.toLowerCase()

        filteredRows = filteredRows.filter((row) => {
            if (row.email.toLowerCase().includes(lSearch)) {
                return true
            }

            if (row.first_name.toLowerCase().includes(lSearch)) {
                return true
            }

            if (row.last_name.toLowerCase().includes(lSearch)) {
                return true
            }

            if (
                row.organization &&
                row.organization.toLowerCase().includes(lSearch)
            ) {
                return true
            }

            if (row.media.toLowerCase().includes(lSearch)) {
                return true
            }

            // match presentation of last name, first name in table
            const lastNameFirstName =
                `${row.last_name}, ${row.first_name}`.toLowerCase()

            if (lastNameFirstName.toLowerCase().includes(lSearch)) {
                return true
            }

            // match natural written  name
            const firstNameLastName = `${row.first_name} ${row.last_name}`
            if (firstNameLastName.toLowerCase().includes(lSearch)) {
                return true
            }

            if (row.task_id.toLowerCase().includes(lSearch)) {
                return true
            }

            return false
        })
    }

    const rowCount = filteredRows.length
    filteredRows = paginate(filteredRows, page, rowsPerPage)

    return (
        <DashboardLayout loading={loading}>
            <SelectedRowDetailsDialog
                onClose={() => setSelectedRow(null)}
                selectedRow={selectedRow}
            />
            <div
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                }}
            >
                <div>
                    <TextField
                        label="Search"
                        variant="standard"
                        onChange={handleSearchChange}
                    />
                </div>
                <div
                    style={{
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            color: 'var(--grey-700)',
                            fontSize: '0.875rem',
                        }}
                    >
                        <strong>{rowCount} results</strong>
                    </p>
                    <p
                        style={{
                            color: 'var(--grey-400)',
                            fontSize: '0.75rem',
                        }}
                    >
                        *orders with a deadline before August 2023 are not shown
                    </p>
                </div>
                <div>
                    <DownloadButton
                        downloading={downloading}
                        onClick={handleDownload}
                    >
                        Download CSV
                    </DownloadButton>
                </div>
            </div>
            <TableContainer
                component={Paper}
                sx={{
                    maxWidth: 'calc(100vw - 240px - 32px)',
                    width: '100%',
                }}
            >
                <Table
                    size="small"
                    sx={{
                        tableLayout: 'fixed',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableHeadCell sx={{ width: 140 }}>
                                Date Submitted
                                <br />
                                <TableCellSubText>Time</TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell
                                sx={{
                                    width: 220,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Name
                                <br />
                                <TableCellSubText>Email</TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell>Media</TableHeadCell>
                            <TableHeadCell>
                                Organization
                                <br />
                                <TableCellSubText>Initials</TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell sx={{ width: 120 }}>
                                Status
                            </TableHeadCell>
                            <TableHeadCell sx={{ width: 160 }}>
                                Lateness
                                <br />
                                <TableCellSubText>Deadline</TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell sx={{ width: 80 }}>
                                Origin
                            </TableHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No results found
                                    <br />
                                    <TableCellSubText>
                                        Try changing your search or filters
                                    </TableCellSubText>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredRows.map((row, index) => {
                                const dateTimeSubmitted = prettyDate(
                                    row.date_submitted
                                ).split(' ')
                                const dateSubmitted = dateTimeSubmitted[0]
                                const timeSubmitted = dateTimeSubmitted[1]

                                return (
                                    <TableRow
                                        key={index}
                                        onClick={() => {
                                            // set the selected row info
                                            // which will also open up a
                                            // modal with more information
                                            if (!isSelectingText()) {
                                                setSelectedRow(row)
                                            }
                                        }}
                                        hover
                                    >
                                        <TableCell>
                                            {dateSubmitted}
                                            <br />
                                            <TableCellSubText>
                                                {timeSubmitted}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.last_name}, {row.first_name}
                                            <br />
                                            <TableCellSubText>
                                                {row.email}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.media}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.organization}
                                            <br />
                                            <TableCellSubText>
                                                {toInitials(
                                                    row.organization || ''
                                                )}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell>{row.status}</TableCell>
                                        <TableCell>
                                            {row.lateness}
                                            <br />
                                            <TableCellSubText>
                                                {prettyDate(row.deadline)}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell>
                                            {row.origin === 'NTE App'
                                                ? 'NTE'
                                                : row.origin === 'NTE Hub'
                                                  ? 'NTE'
                                                  : row.origin ===
                                                      'Messenger Pigeon'
                                                    ? 'MP'
                                                    : row.origin}
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rowCount}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                page={page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, { value: -1, label: 'All' }]}
            />
        </DashboardLayout>
    )
}

export default OverdueOrdersReportPage
